import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { NEWS, NEWS_BOILERPLATE, NEWS_BY_SLUG } from "@/data/news";

/**
 * /news/:slug — standalone, shareable milestone page (press-release format).
 */
const ShareLinks = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/news/${item.slug}`
      : `https://krprivatecapital.com/news/${item.slug}`;
  const text = `${item.title} — Kellermann Reid & Co.`;

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt("Copy this link", url);
    }
  }, [url]);

  const ghost = "kr-btn-ghost";
  const ghostStyle = { padding: "0.6rem 0.95rem", fontSize: "9.5px" };

  return (
    <div className="flex flex-wrap gap-2" data-testid={LANDING.newsDetailShare}>
      <button
        type="button"
        onClick={copy}
        data-testid={LANDING.newsDetailShareCopy}
        className={ghost}
        style={ghostStyle}
      >
        {copied ? "Link Copied ✓" : "Copy Link"}
      </button>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noreferrer"
        data-testid={LANDING.newsDetailShareLinkedin}
        className={ghost}
        style={ghostStyle}
      >
        LinkedIn ↗
      </a>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
        target="_blank"
        rel="noreferrer"
        data-testid={LANDING.newsDetailShareWhatsapp}
        className={ghost}
        style={ghostStyle}
      >
        WhatsApp ↗
      </a>
      <a
        href={`mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${item.body}\n\n${url}`)}`}
        data-testid={LANDING.newsDetailShareEmail}
        className={ghost}
        style={ghostStyle}
      >
        Email
      </a>
    </div>
  );
};

const Meta = ({ label, children }) => (
  <div>
    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-2">
      {label}
    </div>
    <div className="text-[14px] text-[var(--kr-text)]">{children}</div>
  </div>
);

const NewsDetailPage = () => {
  const { slug } = useParams();
  const item = NEWS_BY_SLUG[slug];
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!item) return <Navigate to="/news" replace />;

  const idx = NEWS.findIndex((n) => n.slug === slug);
  const newer = idx > 0 ? NEWS[idx - 1] : null;
  const older = idx < NEWS.length - 1 ? NEWS[idx + 1] : null;
  const dek = `${item.body.split(". ")[0]}.`;

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.newsDetailPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Newsroom"
          label={`${item.kind} · Documented ${item.date}`}
          title={item.title}
          description={dek}
          breadcrumb={`Newsroom / ${item.kind}`}
        />

        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-32 space-y-6">
                <Meta label="Documented">{item.date}</Meta>
                <Meta label="Category">{item.kind}</Meta>
                <div>
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    Share this milestone
                  </div>
                  <ShareLinks item={item} />
                </div>
                <div className="kr-rule" />
                <Link
                  to="/news"
                  data-testid={LANDING.newsDetailBack}
                  className="kr-link font-mono-label text-[11px]"
                >
                  ← Full Newsroom
                </Link>
              </div>
            </aside>

            <article className="lg:col-span-9 lg:pl-8">
              <p
                data-testid={LANDING.newsDetailBody}
                className="font-serif-display text-[var(--kr-text)] text-[1.25rem] lg:text-[1.4rem] leading-[1.65] border-l-2 border-[var(--kr-gold)] pl-8 mb-10 max-w-[62ch]"
              >
                {item.body}
              </p>

              {item.detail?.map((para, i) => (
                <p
                  key={i}
                  className="text-[var(--kr-text-muted)] text-[16px] leading-[1.95] mb-8 max-w-[68ch]"
                >
                  {para}
                </p>
              ))}

              {item.related?.length > 0 && (
                <div className="mt-12 border-t border-[var(--kr-line)] pt-8">
                  <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-5">
                    Related
                  </div>
                  <ul className="flex flex-col gap-3">
                    {item.related.map((r, i) => (
                      <li key={r.to}>
                        <Link
                          to={r.to}
                          data-testid={`${LANDING.newsDetailRelatedPrefix}${i}`}
                          className="kr-link font-mono-label text-[11px]"
                        >
                          {r.label} →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="kr-rule my-12" />
              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                About Kellermann Reid &amp; Co.
              </div>
              <p className="text-[var(--kr-text-muted)] text-[14px] leading-[1.85] max-w-[68ch]">
                {NEWS_BOILERPLATE}
              </p>
              <p className="mt-6 text-[12px] text-[var(--kr-text-muted)] leading-[1.7] italic max-w-[60ch]">
                Formation milestones are documented for sophisticated readers.
                They are not an offer to sell or a solicitation to buy any
                security and do not constitute investment advice.
              </p>
            </article>
          </div>
        </section>

        {/* Prev / Next + media CTA */}
        <section className="border-t border-[var(--kr-line)] py-16 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              {newer && (
                <Link
                  to={`/news/${newer.slug}`}
                  data-testid={LANDING.newsDetailNewer}
                  className="block group"
                >
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    ← Newer · {newer.date}
                  </div>
                  <div className="font-serif-display text-[var(--kr-text)] text-[1.15rem] leading-[1.35] group-hover:text-[var(--kr-gold)] transition-colors">
                    {newer.title}
                  </div>
                </Link>
              )}
            </div>
            <div className="lg:col-span-4">
              {older && (
                <Link
                  to={`/news/${older.slug}`}
                  data-testid={LANDING.newsDetailOlder}
                  className="block group"
                >
                  <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    Older · {older.date} →
                  </div>
                  <div className="font-serif-display text-[var(--kr-text)] text-[1.15rem] leading-[1.35] group-hover:text-[var(--kr-gold)] transition-colors">
                    {older.title}
                  </div>
                </Link>
              )}
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/contact"
                data-testid={LANDING.newsDetailMedia}
                className="kr-btn-primary"
              >
                Media Enquiries
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default NewsDetailPage;
